import React, { useState } from 'react'
import { Grid, Box, Typography, Button, CircularProgress, FormControl, MenuItem, Select, Alert } from '@mui/material'
import { BuildDropdown } from '../../Components/Inputs'
import { ConfirmActionDialog } from '../../Components/Dialogs'

export const keyStatus = [
  {
    value: 'all',
    label: 'All Keys',
  },
  {
    value: 'active',
    label: 'Active',
  },
  {
    value: 'revoked',
    label: 'Revoked',
  },
]

const KeyCard = ({ value, isAdmin, revokeKey, assignPurpose, rotateKey, replaceLegacyKey }) => {
  const [revoking, setRevoking] = useState()
  const [purpose, setPurpose] = useState('')
  const [pendingCredentialAction, setPendingCredentialAction] = useState(null)
  const [pendingRequest, setPendingRequest] = useState(null)
  const [actionError, setActionError] = useState(null)
  const requestPending = pendingRequest !== null || Boolean(value.apiKeyId && revoking === value.apiKeyId)
  const revoke = () => {
    setRevoking(value.apiKeyId)
    revokeKey({ apiKeyId: value.apiKeyId })
  }

  const runCredentialAction = async ({ name, action, failureMessage }) => {
    if (requestPending) return
    setPendingRequest(name)
    setActionError(null)
    try {
      await action()
    } catch {
      setActionError(failureMessage)
    } finally {
      setPendingRequest(null)
    }
  }

  const confirmCredentialAction = async () => {
    const pendingAction = pendingCredentialAction
    setPendingCredentialAction(null)
    if (pendingAction?.type === 'replace') {
      await runCredentialAction({
        name: 'replace',
        action: () => replaceLegacyKey({ name: value.name, purpose: pendingAction.purpose }),
        failureMessage: 'Legacy-key replacement may have completed. Reload the key list before retrying.',
      })
    }
    if (pendingAction?.type === 'rotate') {
      await runCredentialAction({
        name: 'rotate',
        action: () => rotateKey({ apiKeyId: value.apiKeyId }),
        failureMessage: 'API-key rotation may have completed. Reload the key list before retrying. If a newly active successor exists but its secret was not received, rotate that successor to revoke the inaccessible credential and obtain a new secret; otherwise revoke it before creating a replacement.',
      })
    }
  }

  const confirmation = pendingCredentialAction?.type === 'replace'
    ? {
        title: 'Replace legacy API key?',
        description: 'Replacing this key revokes the current credential immediately. Update the integration with the newly generated key before its next request.',
        confirmLabel: 'Replace key',
      }
    : {
        title: 'Rotate API key?',
        description: 'Rotating this key revokes the current credential immediately. Update every integration with the newly generated key before its next request.',
        confirmLabel: 'Rotate key',
      }

  return (
    <>
      {actionError && <Alert severity="error">{actionError}</Alert>}
      <Grid container elevation={2}>
      <Grid size={2} sx={{display: 'flex', alignItems: 'center', paddingLeft: '1rem'}}>
        <Typography paddingTop="0.58rem" paddingBottom="0.58rem">
          {value.name}
        </Typography>
      </Grid>
      <Grid size={3} sx={{display: 'flex', alignItems: 'center'}}>
        <Typography paddingTop="0.58rem" paddingBottom="0.58rem">
          {value.message}
        </Typography>
      </Grid>
      <Grid size={1} sx={{display: 'flex', alignItems: 'center'}}>
        <Typography paddingTop="0.58rem" paddingBottom="0.58rem">
          {value.status}
        </Typography>
        </Grid>
      <Grid size={2} sx={{display: 'flex', alignItems: 'center'}}>
        <Typography>{value.purpose ? `${value.purpose === 'live' ? 'Live' : 'Test'} traffic` : 'Unclassified'}</Typography>
      </Grid>
      <Grid size={4} sx={{display: 'flex', alignItems: 'center', gap: '0.5rem'}}>
          {isAdmin && value.status === 'active' && !value.purpose && (
            <>
              <FormControl size="small" sx={{ minWidth: 120 }} disabled={requestPending}>
                <Select value={purpose} displayEmpty onChange={(event) => setPurpose(event.target.value)}>
                  <MenuItem value="" disabled>Select traffic</MenuItem>
                  <MenuItem value="live">Live traffic</MenuItem>
                  <MenuItem value="test">Test traffic</MenuItem>
                </Select>
              </FormControl>
              <Button variant="outlined" disabled={!purpose || requestPending} onClick={() => (
                value.legacy || !value.apiKeyId
                  ? setPendingCredentialAction({ type: 'replace', purpose })
                  : runCredentialAction({
                      name: 'assign',
                      action: () => assignPurpose({ apiKeyId: value.apiKeyId, purpose }),
                      failureMessage: 'Could not assign API-key traffic. Reload the key list before retrying.',
                    })
              )}>{value.legacy || !value.apiKeyId ? 'Replace' : 'Assign'}</Button>
            </>
          )}
          {isAdmin && value.apiKeyId && value.status === 'active' && value.purpose && (
            <Button variant="outlined" disabled={requestPending} onClick={() => setPendingCredentialAction({ type: 'rotate' })}>Rotate</Button>
          )}
          {revoking === value.apiKeyId && <CircularProgress />}
          {revoking !== value.apiKeyId && isAdmin && value.apiKeyId && value.status === 'active' && <Button variant='outlined' sx={{
            padding: '0.5rem 4rem',
            color: '#0071f4',
            backgroundColor: '#fff',
            border: '1px solid #0071f4',
            textTransform: 'none',
            borderRadius: '0.5rem',
          }} disabled={requestPending} onClick={revoke}>
            Revoke
          </Button>}
      </Grid>
      </Grid>
      <ConfirmActionDialog
        open={pendingCredentialAction !== null}
        title={confirmation.title}
        description={confirmation.description}
        confirmLabel={confirmation.confirmLabel}
        onCancel={() => setPendingCredentialAction(null)}
        onConfirm={confirmCredentialAction}
      />
    </>
  )
}

const KeySection = ({ loading, keys, isAdmin, status, setStatus, revokeKey, assignPurpose, rotateKey, replaceLegacyKey }) => {
  return (
    <>  
      <Box sx={{
        display: 'flex',
        flexDirection: 'row',
        alignItems: 'flex-start',
        marginBottom: '2rem',
      }}>
        <BuildDropdown currentValue={status} values={keyStatus} setter={(e) => {setStatus(e)}} isSmall disableUnderline={true}/>
      </Box>
      
      <TableHeaders/>
      <KeyList keys={keys} isAdmin={isAdmin} loading={loading} revokeKey={revokeKey} assignPurpose={assignPurpose} rotateKey={rotateKey} replaceLegacyKey={replaceLegacyKey} />
    </>
  )
}

const KeyList = ({ keys, isAdmin, loading, revokeKey, assignPurpose, rotateKey, replaceLegacyKey }) => {
  if (loading && keys.length === 0) {
    return (
      <Box sx={{ textAlign: 'center', width: '100%' }}>
        <CircularProgress />
      </Box>
    )
  }
  if (keys.length === 0) {
    return (
      <Box sx={{ textAlign: 'center', width: '100%' }}>
        <Typography>No keys found</Typography>
      </Box>
    )
  }

  return (
    <Box sx={{
      width: '100%',
      bgcolor: 'background.paper',
    }}>
      {keys.map((val, idx) => (
        <Box key={idx} sx={{ 
          paddingTop: '1rem', 
          paddingBottom: '1rem',
          borderBottom: (idx + 1) < keys.length ? '1px solid #e0e0e0' : 'none',
        }}>
          <KeyCard value={val} isAdmin={isAdmin} revokeKey={revokeKey} assignPurpose={assignPurpose} rotateKey={rotateKey} replaceLegacyKey={replaceLegacyKey} />
        </Box>
      ))}
    </Box>
  )
}

const TableHeaders = () => {
  const headers = [
    {
      name: 'Name',
      flex: 2,
    },
    {
      name: 'Information',
      flex: 3,
    },
    { 
      name: 'Status',
      flex: 1,
    },
    {
      name: 'Traffic',
      flex: 2,
    },
    {
      name: 'Actions',
      flex: 4,
    },
  ]
  
  return (
    <Grid container>
      {headers.map((header, i) => (
        <Grid key={`header_${i}`} size={header.flex}>
          <Typography key={i} noWrap sx={{
            textAlign: header.textAlign || 'start',
            textOverflow: 'ellipsis',
            overflow: 'hidden',
            fontWeight: '600',
            fontSize: '1.5rem',
            color: '#58595b',
          }}>
            {header.name}
          </Typography>
        </Grid>
      ))}
    </Grid>
  )
}

export default KeySection
